import { React, useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Button, Search, Text, MTable, Tooltip, Copy, DeviceStatus, Popover, MLink, XCircle } from "@cisco/spaces-dash-lib-styleguide";
import { NodeContext } from "../../contexts/nodeContext";
import * as BusinessHierarchyService from "../../service/business-hierarchy-service";
import CameraEmpty from "../../assets/images/location-accesspoints/camera_empty_lh2.svg";
import { I18nContext } from "../../contexts/i18nContext";
import useLoader from "../../hooks/useLoader";
import CustomSearch from "../custom-search/custom-search";
import "../../common/common_style.scss";
import "../../common/global_style.scss";
import "./location-switches.scss";

function LocationSwitches(props) {
  const i18n = useContext(I18nContext);
  const { node, switchCount, locationRowMap } = useContext(NodeContext);
  const navigate = useNavigate();
  const [locationNode] = useState({ ...node });
  const [switchListState, setSwitchListState] = useState([]);
  const [switchListFilteredState, setSwitchListFilteredState] = useState([]);
  const [switchListPagedState, setSwitchListPagedState] = useState([]);
  const [searchText, setSearchText] = useState("");
  const rsearchText = useRef("");
  const rtotalNoOfItems = useRef(switchCount);
  const [totalNoOfItems, setTotalNoOfItems] = useState(switchCount);
  const { setLoading } = useLoader();
  const [textColor, setTextColor] = useState("#f7f7f7");
  const [bgColor, setBgColor] = useState({ background: "#656c75" });
  const [showTable, setShowTable] = useState(false);
  const appUserSearchSkeleton = {
    ipAddress: "string",
    macAddress: "string"
  };
  const [copyIcon, setCopyIcon] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const hoveredItemId = useRef(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageRef = useRef(10);
  const [paginationKey, setPaginationKey] = useState(0);

  let switchList = [];
  let switchListFILTERED = [];
  let switchListPaged = [];

  const getStart = (currentPage) => {
    return currentPage * rowsPerPageRef.current;
  };

  const getEnd = (currentPage) => {
    return currentPage * rowsPerPageRef.current + rowsPerPageRef.current;
  };

  const refreshPaginator = () => {
    setPageNumber(0);
    setRowsPerPage(rowsPerPageRef.current);
    switchListPaged = switchListFILTERED;
    setSwitchListPagedState(switchListPaged);
  };

  const getLocationPath = (ap) => {
    try {
      const apId = ap.id;
      const apName = ap.name ? ap.name : "";
      const parentLoc = locationRowMap[apId];
      if (!parentLoc) {
        return apName;
      }
      const locPathIds = parentLoc.locationPath;
      if (locPathIds && locPathIds.length) {
        const locNameArr = locPathIds
          .map((id) => {
            const loc = locationRowMap[id];
            return loc ? loc.name : null;
          })
          .filter((name) => name !== null);
        const locNameStr = locNameArr.length ? locNameArr.join(" > ") : "";
        return locNameStr;
      }
      if (apName) return apName;
      return "";
    } catch (e) {
      return "";
    }
  };

  const getLocationPathTruncated = (ap) => {
    const apName = ap.name ? ap.name : "";
    const locPath = getLocationPath(ap);
    if (locPath) {
      if (locPath.indexOf(" > ") - 1) {
        const count = locPath.split(" > ").length - 1;
        if (count > 1) {
          const arr = locPath.split(" > ");
          const newLocPathArr = [];
          newLocPathArr.push(arr[0]);
          newLocPathArr.push(arr[arr.length - 1]);
          const newLocPath = newLocPathArr.join(" > ... > ");
          return newLocPath;
        }
        return locPath;
      }
      return locPath;
    }
    return apName;
  };

  const goToSetupSwitches = () => {
    const path = `/setup/wirednetwork`;
    navigate(path);
  };

  const filterValue = (event) => {
    setPageNumber(0);
    setTotalNoOfItems(switchCount);
    setSearchText(event.target.value);
    rsearchText.current = event.target.value;
    if (rsearchText.current == "") {
      rtotalNoOfItems.current = switchCount;
      setTotalNoOfItems(switchCount);
    }
    setShowTable(true);
  };

  const copyLocationPath = (locationPath) => {
    navigator.clipboard.writeText(locationPath);
    setIsCopied(true);
    setBgColor({
      ...bgColor,
      background: "#FFFF"
    });
    setTextColor("#545454");
  };

  const getCellValueWithToolTip = ({ row }) => {
    return (
      <div
        onMouseEnter={(val) => {
          console.log(val.currentTarget.attributes.getNamedItem("id").value);
          hoveredItemId.current = val.currentTarget.attributes.getNamedItem("id").value;
          setCopyIcon(true);
        }}
        onMouseLeave={() => {
          setIsCopied(false);
          setCopyIcon(false);
          setBgColor({
            ...bgColor,
            background: "#656c75"
          });
          setTextColor("#f7f7f7");
        }}
        id={row.id}
      >
        <Flex direction="horizontal" gap={10} align="center" justify="flex-start">
          <Popover
            style={bgColor}
            placement="top"
            kind={isCopied ? "default" : "map"}
            title={
              <Flex direction="horizontal" gap={8} align="center" justify="space-between">
                {isCopied && (
                  <>
                    <DeviceStatus status="good" />
                    <Text color={textColor} size="p4" weight="regular">
                      {i18n.t("hierarchy-base.copied-to-clipboard")}
                    </Text>
                  </>
                )}
                {!isCopied && (
                  <Text color={textColor} size="p4" weight="regular">
                    {i18n.t("hierarchy-base.copy-to-clipboard")}
                  </Text>
                )}
              </Flex>
            }
            visible
          >
            <span className="hover-target">
              <Button
                icon={
                  <Copy
                    size={15}
                    onClick={() => {
                      copyLocationPath(row.original.locationPath);
                    }}
                  />
                }
                kind="tertiary"
                size="sm"
              />
            </span>
          </Popover>
          <Tooltip title={row.original.locationPath} placement="bottom">
            <span className="hover-target">{row.original.locationPathTruncated}</span>
          </Tooltip>
        </Flex>
      </div>
    );
  };

  const processSwitchListResponse = (response) => {
    if (response.status) {
      switchList = response.data;
      if (switchList && switchList.length > 0) {
        setShowTable(true);
      }
      setSwitchListState(switchList);
      if (switchList && switchList.length > 0) {
        switchList.forEach((apObj, index) => {
          const updatedApObj = {
            ...apObj,
            locationDetails: { ...apObj.locationDetails }
          };

          const { id: apLocId, name: apLocName } = updatedApObj.locationDetails;
          const apLocData = locationRowMap[apLocId];

          if (apLocData) {
            const prefix = apLocData.parentId && apLocData.parentId === locationNode.id ? `${locationNode.name}/` : `${locationNode.name}/..../`;
            updatedApObj.locationDetails.name = `${prefix}${apLocName}`;
          }

          // Replace the original object in switchList with the updated object
          switchList[index] = updatedApObj;
        });
      }
    } else {
      switchList = [];
      setSwitchListState([]);
    }

    switchListFILTERED = switchList.map((switches) => ({
      ...switches,
      locationPath: getLocationPath(switches?.locationDetails),
      locationPathTruncated: getLocationPathTruncated(switches?.locationDetails)
    }));
    setSwitchListFilteredState(switchListFILTERED);
  };

  const getSwitchList = (currentPageNo = 0) => {
    setLoading(true);
    BusinessHierarchyService.getSwitchList(locationNode.id, getStart(currentPageNo), getEnd(currentPageNo))
      .then((response) => {
        setLoading(false);
        processSwitchListResponse(response);
        if (currentPageNo == 0) {
          refreshPaginator();
        } else {
          switchListPaged = switchListFILTERED;
          setSwitchListPagedState(switchListPaged);
        }
      })
      .catch((error) => {
        setLoading(false);
        setSwitchListState([]);
        switchList = [];
        setSwitchListFilteredState([]);
        switchListFILTERED = [];
        refreshPaginator();
        console.log(`Location Sensors: Error on getSwitchList${error}`);
      });
  };

  const searchList = (data) => {
    switchList = data;
    if (switchList && switchList.length > 0) {
      setShowTable(true);
    }
    setSwitchListState(switchList);
    if (switchList && switchList.length > 0) {
      switchList.forEach((apObj) => {
        const updatedApObj = { ...apObj };
        const { id: apLocId, name: apLocName } = updatedApObj.locationDetails;
        const apLocData = locationRowMap[apLocId];

        if (apLocData) {
          const prefix = apLocData.parentId && apLocData.parentId === locationNode.id ? `${locationNode.name}/` : `${locationNode.name}/..../`;
          updatedApObj.locationDetails.name = `${prefix}${apLocName}`;
        }
        const index = switchList.indexOf(apObj);
        if (index !== -1) {
          switchList[index] = updatedApObj;
        }
      });
    }

    switchListFILTERED = switchList.map((switches) => ({
      ...switches,
      locationPath: getLocationPath(switches?.locationDetails),
      locationPathTruncated: getLocationPathTruncated(switches?.locationDetails)
    }));
    setSwitchListFilteredState(switchListFILTERED);
  };

  const processSwitchListResponseInitial = (response) => {
    if (response.status) {
      switchList = response.data;
      if (switchList && switchList.length > 0) {
        setShowTable(true);
      }
      if (switchCount <= rowsPerPage && switchCount != switchList.length) {
        setTotalNoOfItems(switchList.length);
        rtotalNoOfItems.current = switchList.length;
      } else {
        setTotalNoOfItems(switchCount);
      }
      setSwitchListState(switchList);
      if (switchList && switchList.length > 0) {
        switchList.forEach((apObj, index) => {
          const updatedApObj = {
            ...apObj,
            locationDetails: { ...apObj.locationDetails }
          };

          const { id: apLocId, name: apLocName } = updatedApObj.locationDetails;
          const apLocData = locationRowMap[apLocId];

          if (apLocData) {
            const prefix = apLocData.parentId && apLocData.parentId === locationNode.id ? `${locationNode.name}/` : `${locationNode.name}/..../`;
            updatedApObj.locationDetails.name = `${prefix}${apLocName}`;
          }

          // Replace the original object in switchList with the updated object
          switchList[index] = updatedApObj;
        });
      }
    } else {
      switchList = [];
      setSwitchListState([]);
    }
  };

  const initSwitchList = (currentPageNo = 0) => {
    setLoading(true);
    BusinessHierarchyService.getSwitchList(locationNode.id, getStart(currentPageNo), getEnd(currentPageNo))
      .then((response) => {
        setLoading(false);
        processSwitchListResponseInitial(response);
        if (currentPageNo == 0) {
          refreshPaginator();
        } else {
          switchListPaged = switchListFILTERED;
          setSwitchListPagedState(switchListPaged);
        }
      })
      .catch((error) => {
        setLoading(false);
        setSwitchListState([]);
        switchList = [];
        setSwitchListFilteredState([]);
        switchListFILTERED = [];
        refreshPaginator();
        console.log(`Location Sensors: Error on getSwitchList${error}`);
      });
  };

  const onAdminSearchFilter = (data) => {
    console.log(data);
    if (rsearchText.current.length > 0) {
      setShowTable(true);
      setTotalNoOfItems(data.length);
      rtotalNoOfItems.current = data.length;
      searchList(data);
      refreshPaginator();
    } else {
      setShowTable(true);
      setTotalNoOfItems(switchCount);
      rtotalNoOfItems.current = switchCount;
      getSwitchList(0);
    }
  };

  const onPageChange = (event) => {
    switchListFILTERED = switchListFilteredState;
    switchListPaged = switchListFILTERED;
    setSwitchListPagedState(switchListPaged);
    getSwitchList(event);
  };

  const clearSearch = () => {
    setSearchText("");
    setPageNumber(0);
    setRowsPerPage(10);
    rowsPerPageRef.current = 10;
    rsearchText.current = "";
    setTotalNoOfItems(switchCount);
    rtotalNoOfItems.current = switchCount;
    setShowTable(true);
    getSwitchList();
  };

  useEffect(() => {
    console.log("LocationSwitches Mount");
    console.log(`${props} : ${copyIcon}`);
    if (switchCount && switchCount > 0) {
      initSwitchList();
    }
    return () => {
      console.log("LocationSwitches Destroy");
    };
  }, []);

  useEffect(() => {
    setPaginationKey(paginationKey + 1);
  }, [switchListPagedState]);

  return (
    <div className="ap-wrapper boxShadowBlur" id="switch-wrapper">
      <Flex direction="vertical" gap={16}>
        <Flex align="center" justify="space-between" gap={8} />
        {showTable && switchListPagedState && switchListPagedState.length >= 0 && (
          <Flex align="center" justify="space-between">
            <Flex justify="space-between">
              <Flex align="center" gap={16}>
                <Flex gap={8}>
                  <Search
                    label=""
                    placeholder={i18n.t("proximity-rule.search-text")}
                    options={[]}
                    onChange={filterValue}
                    value={searchText}
                    suffix={searchText.length > 0 && <XCircle title="clear search" size={18} onClick={clearSearch} />}
                  />
                  <CustomSearch
                    searchSkeleton={appUserSearchSkeleton}
                    customSearchText={searchText.toLowerCase()}
                    shouldUseGlobalSearch={false}
                    rawData={switchListState}
                    onAdminSearchFilter={onAdminSearchFilter}
                  />
                </Flex>
              </Flex>
            </Flex>
            <Flex justify="space-between">
              <Flex align="center">
                {showTable && switchListPagedState && switchListPagedState.length > 0 && (
                  <MLink onClick={goToSetupSwitches} size="md">
                    {i18n.t("location-sensors.on-board-switches-text")}
                  </MLink>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
        {showTable && switchListPagedState && switchListPagedState.length >= 0 && (
          <div className="userTableContainer">
            <MTable.Pagination
              currentPage={pageNumber + 1}
              paginationKey={paginationKey}
              key={switchListPagedState.length}
              defaultRecordsOption={rowsPerPageRef.current}
              kind={
                rsearchText.current && rsearchText.current != ""
                  ? "client"
                  : {
                      server: {
                        rowCount: totalNoOfItems,
                        onPageChange: (currentPage) => {
                          setPageNumber(currentPage);
                          onPageChange(currentPage);
                        },
                        onRowsPerPageChange: (change) => {
                          rowsPerPageRef.current = change;
                          setRowsPerPage(change);
                          getSwitchList(0);
                        }
                      }
                    }
              }
              showNavigation="all"
              showOptions
              showTotal
            >
              <MTable
                columns={[
                  {
                    accessorKey: "macAddress",
                    header: i18n.t("business-location-details.mac-address-text"),
                    meta: {
                      align: "left"
                    },
                    enableSorting: true
                  },
                  {
                    accessorKey: "locationPathTruncated",
                    cell: getCellValueWithToolTip,
                    header: i18n.t("location-access-points.location-text"),
                    meta: {
                      align: "left"
                    }
                  },
                  {
                    accessorKey: "ipAddress",
                    header: i18n.t("location-switches.switch-ip-text"),
                    meta: {
                      align: "left"
                    },
                    enableSorting: true
                  }
                ]}
                data={switchListPagedState}
              />
            </MTable.Pagination>
          </div>
        )}
        {!showTable && (!rsearchText.current || rsearchText.current.length == 0) && switchListPagedState.length == 0 && (
          <div className="team-empty-business">
            <div className="divTable empty_wrapper" id="network-switches-text">
              <div className="divTableRow">
                <div className="divTableCell width50_middle">
                  <div className="title">
                    <Text size="p1" weight="bold">
                      {i18n.t("location-switches.there-are-no-switches-text")}
                    </Text>
                  </div>
                  <div className="desc" id="switches-text-title">
                    <Text size="p2" weight="regular" color="light">
                      {i18n.t("location-switches.configure-switches-text")}
                    </Text>
                  </div>
                  <Button type="primary" onClick={goToSetupSwitches}>
                    {i18n.t("location-switches.configure-wired-network")}
                  </Button>
                </div>
                <div className="divTableCell width50_middle">
                  <div>
                    <img alt="switches missing" className="team-empty-img" src={CameraEmpty} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Flex>
    </div>
  );
}

export default LocationSwitches;
